import React, { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { tokens } from '../../mui-theme';
import { SETTINGS_SIDEBAR_ITEMS } from '../../ui/layout/sidebar/SidebarMenuItems';
import { useRole } from '../../hooks/useRole';

export const SettingsListItems = ({ open }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [submenuOpen, setSubmenuOpen] = useState({});
  const role = useRole();

  const rawSidebarItems = SETTINGS_SIDEBAR_ITEMS();

  const sidebarItems = rawSidebarItems
    .map((item) => {
      if (item.submenu) {
        return {
          ...item,
          submenu: item.submenu.filter((s) => !s.roles || s.roles.includes(role)),
        };
      }
      return item;
    })
    .filter((item) => {
      if (item.submenu) {
        return item.submenu.length > 0;
      }
      return !item.roles || item.roles.includes(role);
    });

  const getView = () => new URLSearchParams(location.search).get('view');

  const isItemActive = (item) => {
    if (item.submenu) return item.submenu.some((s) => s.param === getView());
    return item.param === getView();
  };

  const isChildActive = (param) => getView() === param;

  const setView = (param, url) => {
    navigate(`${url}?view=${param}`);
  };

  // Auto-expand parent if a child is currently active
  useEffect(() => {
    sidebarItems.forEach((item) => {
      if (item.submenu?.some((s) => s.param === getView())) {
        setSubmenuOpen((prev) => ({ ...prev, [item.id]: true }));
      }
    });
  }, [location.search, role]);

  const handleItemClick = (item) => {
    if (item.submenu) {
      if (!open) {
        // Collapsed: jump straight to first child
        setView(item.submenu[0].param, item.url);
      } else {
        setSubmenuOpen((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
      }
    } else {
      setView(item.param, item.url);
    }
  };

  return (
    <>
      {sidebarItems.map((item) => {
        const hasSubmenu  = !!item.submenu;
        const isActive    = isItemActive(item);
        const isExpanded  = submenuOpen[item.id];

        return (
          <React.Fragment key={item.id}>
            {/* Parent item */}
            <Box sx={{ px: open ? 1 : 0.5, mb: 0.25 }}>
              <Tooltip title={!open ? item.label : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  selected={isActive}
                  sx={{
                    borderRadius: '6px',
                    py: 0.75,
                    px: open ? 1.5 : 1,
                    minHeight: 40,
                    position: 'relative',
                    justifyContent: open ? 'flex-start' : 'center',
                    backgroundColor: isActive
                      ? alpha(tokens.colors.primary[500], 0.08)
                      : 'transparent',
                    transition: 'background-color 0.15s',
                    '&:hover': {
                      backgroundColor: isActive
                        ? alpha(tokens.colors.primary[500], 0.11)
                        : tokens.colors.neutral[100],
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(tokens.colors.primary[500], 0.08),
                      '&:hover': { backgroundColor: alpha(tokens.colors.primary[500], 0.11) },
                    },
                    ...(isActive && open && {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '18%',
                        height: '64%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: tokens.colors.primary[500],
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: open ? 36 : 'unset',
                      color: isActive ? tokens.colors.primary[500] : tokens.colors.neutral[500],
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  {open && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? tokens.colors.primary[500] : tokens.colors.neutral[700],
                        lineHeight: 1.4,
                        noWrap: true,
                      }}
                      sx={{ m: 0 }}
                    />
                  )}

                  {hasSubmenu && open && (
                    <Box
                      sx={{
                        color: isActive ? tokens.colors.primary[500] : tokens.colors.neutral[400],
                        display: 'flex',
                        alignItems: 'center',
                        ml: 0.5,
                      }}
                    >
                      {isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                    </Box>
                  )}
                </ListItemButton>
              </Tooltip>
            </Box>

            {/* Submenu */}
            {hasSubmenu && open && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pb: 0.5 }}>
                  {item.submenu.map((child) => {
                    const isSubActive = isChildActive(child.param);
                    return (
                      <Box key={child.id} sx={{ pl: 3, pr: 1, mb: 0.25 }}>
                        <ListItemButton
                          onClick={() => setView(child.param, item.url)}
                          selected={isSubActive}
                          sx={{
                            borderRadius: '6px',
                            py: 0.6,
                            px: 1.5,
                            minHeight: 32,
                            backgroundColor: isSubActive
                              ? alpha(tokens.colors.primary[500], 0.07)
                              : 'transparent',
                            transition: 'background-color 0.15s',
                            '&:hover': {
                              backgroundColor: isSubActive
                                ? alpha(tokens.colors.primary[500], 0.1)
                                : tokens.colors.neutral[100],
                            },
                            '&.Mui-selected': {
                              backgroundColor: alpha(tokens.colors.primary[500], 0.07),
                            },
                            borderLeft: `2px solid ${
                              isSubActive ? tokens.colors.primary[400] : tokens.colors.neutral[200]
                            }`,
                          }}
                        >
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: '0.78rem',
                              fontWeight: isSubActive ? 600 : 400,
                              color: isSubActive
                                ? tokens.colors.primary[500]
                                : tokens.colors.neutral[600],
                              lineHeight: 1.4,
                            }}
                            sx={{ m: 0 }}
                          />
                        </ListItemButton>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};